import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody, ModalFooter } from '@ingka/modal';
import Button from '@ingka/button';
import useYupValidationResolver from '@/lib/hooks/yup';
import { useContentTracking } from '@/lib/hooks/tracking';
import { ItemStatus, Location } from '@/redux/back-dashboard-api';
import {
  UpdateOrderItemStatusFormData,
  updateOrderItemStatusSchema,
} from '@/lib/schemas/update-order-item-status';
import { uniq } from '@pcmp-checka/lib-utilities/array';
import { ItemStatusDropdown } from '@/components/forms/item-status-dropdown';
import { OrderItemListItem } from '@/components/lists/order-item';
import ControlledText from '@/components/forms/controlled-text';
import { OrderWithScope } from '@/redux/back-dashboard-api/hooks';
import { getItemStatus } from '@/lib/utils/items-status-machine';
import {
  useActiveLocation,
  useActiveLocationPermissions,
} from '@/redux/features/shopping-bag/hooks';
import ControlledCombo, {
  ComboItem,
} from '@/components/forms/controlled-combo';
import InlineMessage from '@ingka/inline-message';

type OrderItem = OrderWithScope['items'][number];
type OrderEntry = OrderItem & { orderId: string; orderName: string };
type OrderGroup = {
  from: ItemStatus;
  orders: {
    orderId: string;
    orderName: string;
    items: OrderItem[];
  }[];
};

const useGroupOrdersByStatus = (orderItems: OrderEntry[]): OrderGroup[] => {
  const statuses = useMemo(
    () => uniq(orderItems.map(({ lastStatus }) => lastStatus)),
    [orderItems]
  );
  const orders = useMemo(() => {
    const ordersMap = orderItems.reduce<
      Record<
        string,
        { orderId: string; orderName: string; orderItems: OrderItem[] }
      >
    >((acc, { orderId, orderName, ...orderItem }) => {
      if (!acc[orderId]) {
        acc[orderId] = {
          orderId,
          orderName,
          orderItems: [],
        };
      }

      acc[orderId].orderItems.push(orderItem);

      return acc;
    }, {});

    return Object.values(ordersMap);
  }, [orderItems]);

  const groups = useMemo(
    () =>
      statuses.map((from) => {
        const data = {
          from,
          orders: orders
            .map(({ orderId, orderName, orderItems }) => ({
              orderId,
              orderName,
              items: orderItems.filter(({ lastStatus }) => lastStatus === from),
            }))
            .filter(({ items }) => items.length > 0),
        };

        return data;
      }),
    [statuses, orders]
  );

  return groups;
};

const OrderHeader: FC<{
  name: string;
}> = ({ name }) => (
  <div className="py-2 border-b">
    <span className="truncate">
      <Trans i18nKey="label.orderId" values={{ id: name }} />{' '}
    </span>
  </div>
);

const OrderStatusGroup: FC<{
  index: number;
  onChange: (to: ItemStatus) => void;
  group: OrderGroup;
  location: Location;
}> = ({ index, onChange, group, location }) => {
  // Hooks
  const { t } = useTranslation();
  const { isOrderAdmin } = useActiveLocationPermissions();
  // Data
  const from = useWatch<UpdateOrderItemStatusFormData, `items.${number}.from`>({
    name: `items.${index}.from`,
  });
  const to = useWatch<UpdateOrderItemStatusFormData, `items.${number}.to`>({
    name: `items.${index}.to`,
  });
  const { orders } = group;

  // Filter out Collected status if users can't set it (admins always can)
  const availableStatuses = useMemo(() => {
    const statuses = orders[0].items[0].nextUpdatableStatus;
    const canSetCollected =
      location?.settings.bookings.usersCanSetCollectedState ?? false;

    if (isOrderAdmin || canSetCollected) {
      return statuses;
    }

    return statuses.filter((status) => status !== ItemStatus.Collected);
  }, [orders, location, isOrderAdmin]);

  const dropdownReasons = useMemo(
    () =>
      getItemStatus(to).reasons.map((reason) => ({
        value: reason,
        i18nKey: `label.order.reason.${reason}`,
      })),
    [to]
  );
  const dropdownWarehouses = useMemo(
    () =>
      location?.warehouses.map(
        (warehouse) =>
          ({
            value: warehouse._id,
            i18nKey: warehouse.name,
          } as ComboItem)
      ) || [],
    [location]
  );

  const enrichedWarehouses = useMemo(() => {
    const items = group.orders.flatMap((order) => order.items);

    const groupedItemIds = items.reduce<Record<string, number>>((acc, item) => {
      const itemId = item.item._id;
      acc[itemId] = (acc[itemId] || 0) + 1;
      return acc;
    }, {});

    return dropdownWarehouses.map((warehouse) => {
      const warehouseId = warehouse.value;

      const hasInsufficientStock = Object.entries(groupedItemIds).some(
        ([itemId, requiredAmount]) => {
          const itemStock = location?.stock.find(
            (stock) => stock.item._id === itemId
          );
          const stock = itemStock?.availability.find(
            (availability) => availability._warehouseId === warehouseId
          );

          const availableAmount = stock?.amount ?? 0;

          // Set quantity label for single item case
          if (Object.keys(groupedItemIds).length === 1) {
            warehouse.quantityLabel = availableAmount;
          }

          return availableAmount < requiredAmount;
        }
      );

      return { ...warehouse, disabled: hasInsufficientStock };
    });
  }, [dropdownWarehouses, group, location]);

  const isOutOfStock = useMemo(
    () => enrichedWarehouses.every((warehouse) => warehouse.disabled),
    [enrichedWarehouses]
  );

  return (
    <div className={`flex flex-col px-2 pb-12`}>
      <div className="py-2 border-b flex flex-roaw justify-end items-end">
        <ItemStatusDropdown
          selected={to}
          onChange={onChange}
          options={availableStatuses}
          data-testid="itemStatusDropdown"
        />
      </div>

      {orders.map(({ orderName, items }) => (
        <div key={`${from}-${orderName}`}>
          <OrderHeader name={orderName} />

          {items.map(({ _orderItemId, item }) => (
            <OrderItemListItem key={_orderItemId} item={item} />
          ))}
        </div>
      ))}

      {from !== to && (
        <div className="mt-2">
          {to === ItemStatus.Prepared && dropdownWarehouses.length > 0 && (
            <>
              <ControlledCombo<UpdateOrderItemStatusFormData>
                name={`items.${index}.warehouseId`}
                label={t('label.storage')}
                items={enrichedWarehouses}
                readOnly={isOutOfStock}
                showPills={false}
              />
              {isOutOfStock && (
                <InlineMessage
                  className="mb-2"
                  variant="negative"
                  body={t('label.insufficientStock')}
                />
              )}
            </>
          )}

          {dropdownReasons.length > 0 && (
            <ControlledCombo<UpdateOrderItemStatusFormData>
              name={`items.${index}.reason`}
              label={t('label.reason')}
              items={dropdownReasons}
              showPills={false}
            />
          )}
        </div>
      )}
    </div>
  );
};

const UpdateOrderItemStatusSider: FC<
  ModalComponentBaseProps & {
    orderItems: OrderEntry[];
    onUpdate: (
      data: {
        orderId: string;
        orderItemId: string;
        status: ItemStatus;
        reason?: string;
        warehouseId?: string;
      }[],
      comments: string
    ) => Promise<boolean>;
  }
> = ({ handleDismiss, onUpdate, orderItems, ...rest }) => {
  // Hooks
  const { t } = useTranslation();
  const trackContent = useContentTracking();
  const isSubmitting = useRef(false);
  // Data
  const { data: location } = useActiveLocation();
  const groups = useGroupOrdersByStatus(orderItems);
  // Form
  const defaultItems = groups.map(({ from }) => ({
    from,
    to: from,
    reason: '',
    warehouseId: '',
  }));
  const resolver = useYupValidationResolver(updateOrderItemStatusSchema);
  const form = useForm<UpdateOrderItemStatusFormData>({
    mode: 'all',
    resolver,
    defaultValues: { items: defaultItems },
    criteriaMode: 'all',
  });
  const isDisabled = !form.formState.isValid || isSubmitting.current;
  // Event handlers
  const handleChangeValue = useCallback(
    (index: number) => (to: ItemStatus) => {
      form.setValue(`items.${index}.warehouseId`, '');
      form.setValue(`items.${index}.reason`, '');
      form.setValue(`items.${index}.to`, to, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form]
  );
  const handleUpdate = form.handleSubmit(async (formData) => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;

    const data = groups.flatMap(({ from, orders }, index) => {
      const status = formData.items[index].to;
      const reason = formData.items[index].reason || undefined; // Remove empty string
      const warehouseId = formData.items[index].warehouseId || undefined; // Remove empty string

      return orders.flatMap(({ orderId, items }) =>
        items.map(({ _orderItemId }) => ({
          orderId: orderId,
          orderItemId: _orderItemId,
          status,
          reason,
          warehouseId,
        }))
      );
    });

    try {
      const result = await onUpdate(data, formData.comments);

      if (result) handleDismiss();
    } finally {
      isSubmitting.current = false;
    }
  });

  useEffect(() => {
    trackContent('update-order-item-status', { type: 'sider' });
  }, []);

  return (
    <Sheets
      {...rest}
      data-testid="updateOrderItemStatusSider"
      aria-label={t('label.updateOrder')}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t('label.updateOrder')}
          backBtnClick={handleDismiss}
        />
      }
      footer={
        <ModalFooter className="gap-x-4">
          <Button type="secondary" onClick={handleDismiss}>
            {t('label.cancel')}
          </Button>

          <Button type="primary" disabled={isDisabled} onClick={handleUpdate}>
            {t('label.confirm')}
          </Button>
        </ModalFooter>
      }
    >
      <ModalBody>
        <FormProvider {...form}>
          <ControlledText<UpdateOrderItemStatusFormData>
            name="comments"
            label={t('label.comments')}
          />

          {groups.map(({ from, orders }, index) => (
            <OrderStatusGroup
              key={from}
              index={index}
              onChange={handleChangeValue(index)}
              group={{ from, orders }}
              location={location!}
            />
          ))}
        </FormProvider>
      </ModalBody>
    </Sheets>
  );
};

export default UpdateOrderItemStatusSider;
